import { useState, useEffect } from "react";
import {
  FileText,
  Download,
  Printer,
  Calendar,
  DollarSign,
  TrendingUp,
  Users,
  Activity,
  Filter,
} from "lucide-react";
import medicalDB from "../services/database";

const FinancialReports = () => {
  const [reportType, setReportType] = useState("summary");
  const [dateRange, setDateRange] = useState("month");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Set default date range
    const now = new Date();
    const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDayOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);

    setStartDate(firstDayOfMonth.toISOString().split("T")[0]);
    setEndDate(lastDayOfMonth.toISOString().split("T")[0]);
  }, []);

  useEffect(() => {
    if (startDate && endDate) {
      generateReport();
    }
  }, [reportType, startDate, endDate]);

  const generateReport = async () => {
    try {
      setLoading(true);
      const start = new Date(startDate);
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999); // End of day

      let data = {};

      switch (reportType) {
        case "summary":
          data = await generateSummaryReport(start, end);
          break;
        case "detailed":
          data = await generateDetailedReport(start, end);
          break;
        case "patient":
          data = await generatePatientReport(start, end);
          break;
        case "service":
          data = await generateServiceReport(start, end);
          break;
        default:
          data = await generateSummaryReport(start, end);
      }

      setReportData(data);
    } catch (error) {
      console.error("Error generating report:", error);
      setReportData(null);
    } finally {
      setLoading(false);
    }
  };

  const generateSummaryReport = async (startDate, endDate) => {
    const transactions = await medicalDB.getAllTransactions({
      dateFrom: startDate,
      dateTo: endDate,
    });

    const totalRevenue = transactions.reduce(
      (sum, t) => sum + t.totalAmount,
      0
    );
    const totalPaid = transactions.reduce((sum, t) => sum + t.paidAmount, 0);
    const totalOutstanding = totalRevenue - totalPaid;

    const paymentMethods = {};
    const dailyRevenue = {};

    transactions.forEach((transaction) => {
      // Payment methods breakdown
      const method = transaction.paymentMethod || "unknown";
      paymentMethods[method] =
        (paymentMethods[method] || 0) + transaction.paidAmount;

      // Daily revenue
      const date = new Date(transaction.date).toISOString().split("T")[0];
      dailyRevenue[date] = (dailyRevenue[date] || 0) + transaction.totalAmount;
    });

    const uniquePatients = new Set(transactions.map((t) => t.patientId)).size;

    return {
      type: "summary",
      period: { startDate, endDate },
      metrics: {
        totalRevenue,
        totalPaid,
        totalOutstanding,
        totalTransactions: transactions.length,
        uniquePatients,
        averageTransactionValue:
          transactions.length > 0 ? totalRevenue / transactions.length : 0,
      },
      paymentMethods,
      dailyRevenue,
      transactions: transactions.slice(0, 10), // Recent 10 for preview
    };
  };

  const generateDetailedReport = async (startDate, endDate) => {
    const transactions = await medicalDB.getAllTransactions({
      dateFrom: startDate,
      dateTo: endDate,
    });

    return {
      type: "detailed",
      period: { startDate, endDate },
      transactions,
      summary: {
        totalRevenue: transactions.reduce((sum, t) => sum + t.totalAmount, 0),
        totalPaid: transactions.reduce((sum, t) => sum + t.paidAmount, 0),
        totalTransactions: transactions.length,
      },
    };
  };

  const generatePatientReport = async (startDate, endDate) => {
    const transactions = await medicalDB.getAllTransactions({
      dateFrom: startDate,
      dateTo: endDate,
    });

    const patientSummary = {};
    transactions.forEach((transaction) => {
      const patientId = transaction.patientId;
      const patientName = transaction.patient?.name || "Unknown";

      if (!patientSummary[patientId]) {
        patientSummary[patientId] = {
          name: patientName,
          totalRevenue: 0,
          totalPaid: 0,
          transactionCount: 0,
          transactions: [],
        };
      }

      patientSummary[patientId].totalRevenue += transaction.totalAmount;
      patientSummary[patientId].totalPaid += transaction.paidAmount;
      patientSummary[patientId].transactionCount += 1;
      patientSummary[patientId].transactions.push(transaction);
    });

    const sortedPatients = Object.values(patientSummary).sort(
      (a, b) => b.totalRevenue - a.totalRevenue
    );

    return {
      type: "patient",
      period: { startDate, endDate },
      patients: sortedPatients,
      summary: {
        totalPatients: Object.keys(patientSummary).length,
        totalRevenue: transactions.reduce((sum, t) => sum + t.totalAmount, 0),
        averagePerPatient:
          Object.keys(patientSummary).length > 0
            ? transactions.reduce((sum, t) => sum + t.totalAmount, 0) /
              Object.keys(patientSummary).length
            : 0,
      },
    };
  };

  const generateServiceReport = async (startDate, endDate) => {
    const transactions = await medicalDB.getAllTransactions({
      dateFrom: startDate,
      dateTo: endDate,
    });

    const serviceBreakdown = {
      visits: {},
      actions: {},
      medications: {},
    };

    transactions.forEach((transaction) => {
      transaction.items?.forEach((item) => {
        switch (item.type) {
          case "visit":
            serviceBreakdown.visits[item.name] =
              (serviceBreakdown.visits[item.name] || 0) + item.price;
            break;
          case "action":
            serviceBreakdown.actions[item.name] =
              (serviceBreakdown.actions[item.name] || 0) + item.price;
            break;
          case "medication":
            serviceBreakdown.medications[item.name] =
              (serviceBreakdown.medications[item.name] || 0) + item.price;
            break;
        }
      });
    });

    return {
      type: "service",
      period: { startDate, endDate },
      serviceBreakdown,
      summary: {
        totalVisitRevenue: Object.values(serviceBreakdown.visits).reduce(
          (sum, val) => sum + val,
          0
        ),
        totalActionRevenue: Object.values(serviceBreakdown.actions).reduce(
          (sum, val) => sum + val,
          0
        ),
        totalMedicationRevenue: Object.values(
          serviceBreakdown.medications
        ).reduce((sum, val) => sum + val, 0),
      },
    };
  };

  const exportReport = (format = "csv") => {
    if (!reportData) return;

    let csvContent = "";
    const filename = `financial-report-${reportType}-${startDate}-to-${endDate}.${format}`;

    switch (reportData.type) {
      case "summary":
        csvContent = generateSummaryCSV(reportData);
        break;
      case "detailed":
        csvContent = generateDetailedCSV(reportData);
        break;
      case "patient":
        csvContent = generatePatientCSV(reportData);
        break;
      case "service":
        csvContent = generateServiceCSV(reportData);
        break;
    }

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const generateSummaryCSV = (data) => {
    let csv = "Financial Summary Report\n";
    csv += `Period: ${new Date(
      data.period.startDate
    ).toLocaleDateString()} - ${new Date(
      data.period.endDate
    ).toLocaleDateString()}\n\n`;

    csv += "Metrics\n";
    csv +=
      "Total Revenue,Total Paid,Outstanding,Transactions,Unique Patients,Average Transaction\n";
    csv += `${data.metrics.totalRevenue.toFixed(
      2
    )},${data.metrics.totalPaid.toFixed(
      2
    )},${data.metrics.totalOutstanding.toFixed(2)},${
      data.metrics.totalTransactions
    },${
      data.metrics.uniquePatients
    },${data.metrics.averageTransactionValue.toFixed(2)}\n\n`;

    csv += "Payment Methods\n";
    csv += "Method,Amount\n";
    Object.entries(data.paymentMethods).forEach(([method, amount]) => {
      csv += `${method},${amount.toFixed(2)}\n`;
    });

    return csv;
  };

  const generateDetailedCSV = (data) => {
    let csv = "Detailed Transaction Report\n";
    csv += `Period: ${new Date(
      data.period.startDate
    ).toLocaleDateString()} - ${new Date(
      data.period.endDate
    ).toLocaleDateString()}\n\n`;

    csv +=
      "Receipt Number,Date,Patient,Total Amount,Paid Amount,Outstanding,Payment Method,Status\n";
    data.transactions.forEach((transaction) => {
      csv += `${transaction.receiptNumber},${new Date(
        transaction.date
      ).toLocaleDateString()},${
        transaction.patient?.name || "Unknown"
      },${transaction.totalAmount.toFixed(2)},${transaction.paidAmount.toFixed(
        2
      )},${(transaction.totalAmount - transaction.paidAmount).toFixed(2)},${
        transaction.paymentMethod || "N/A"
      },${transaction.status}\n`;
    });

    return csv;
  };

  const generatePatientCSV = (data) => {
    let csv = "Patient Revenue Report\n";
    csv += `Period: ${new Date(
      data.period.startDate
    ).toLocaleDateString()} - ${new Date(
      data.period.endDate
    ).toLocaleDateString()}\n\n`;

    csv +=
      "Patient Name,Total Revenue,Total Paid,Outstanding,Transaction Count\n";
    data.patients.forEach((patient) => {
      csv += `${patient.name},${patient.totalRevenue.toFixed(
        2
      )},${patient.totalPaid.toFixed(2)},${(
        patient.totalRevenue - patient.totalPaid
      ).toFixed(2)},${patient.transactionCount}\n`;
    });

    return csv;
  };

  const generateServiceCSV = (data) => {
    let csv = "Service Revenue Report\n";
    csv += `Period: ${new Date(
      data.period.startDate
    ).toLocaleDateString()} - ${new Date(
      data.period.endDate
    ).toLocaleDateString()}\n\n`;

    csv += "Visit Types\n";
    csv += "Service,Revenue\n";
    Object.entries(data.serviceBreakdown.visits).forEach(
      ([service, revenue]) => {
        csv += `${service},${revenue.toFixed(2)}\n`;
      }
    );

    csv += "\nMedical Actions\n";
    csv += "Action,Revenue\n";
    Object.entries(data.serviceBreakdown.actions).forEach(
      ([action, revenue]) => {
        csv += `${action},${revenue.toFixed(2)}\n`;
      }
    );

    csv += "\nMedications\n";
    csv += "Medication,Revenue\n";
    Object.entries(data.serviceBreakdown.medications).forEach(
      ([medication, revenue]) => {
        csv += `${medication},${revenue.toFixed(2)}\n`;
      }
    );

    return csv;
  };

  const printReport = () => {
    if (!reportData) return;

    const printWindow = window.open("", "_blank");
    const reportHtml = generateReportHTML(reportData);

    printWindow.document.write(reportHtml);
    printWindow.document.close();
  };

  const generateReportHTML = (data) => {
    const currentDate = new Date().toLocaleDateString();
    const currentTime = new Date().toLocaleTimeString();

    return `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Financial Report - ${data.type}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #333; padding-bottom: 20px; }
            .metrics { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 20px; margin: 20px 0; }
            .metric-card { border: 1px solid #ddd; padding: 15px; border-radius: 5px; }
            .metric-value { font-size: 24px; font-weight: bold; color: #2563eb; }
            .metric-label { color: #666; font-size: 14px; }
            table { width: 100%; border-collapse: collapse; margin: 20px 0; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f5f5f5; }
            .footer { margin-top: 30px; text-align: center; color: #666; font-size: 12px; }
            @media print {
              body { margin: 0; }
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Financial Report - ${
              data.type.charAt(0).toUpperCase() + data.type.slice(1)
            }</h1>
            <p>Period: ${new Date(
              data.period.startDate
            ).toLocaleDateString()} - ${new Date(
      data.period.endDate
    ).toLocaleDateString()}</p>
          </div>
          
          ${generateReportContent(data)}
          
          <div class="footer">
            <p>Generated on ${currentDate} at ${currentTime}</p>
            <p>Medical Prototype - Financial Management System</p>
          </div>
          
          <script>
            window.onload = function() {
              window.print();
              window.onafterprint = function() {
                window.close();
              };
            };
          </script>
        </body>
      </html>
    `;
  };

  const generateReportContent = (data) => {
    switch (data.type) {
      case "summary":
        return `
          <div class="metrics">
            <div class="metric-card">
              <div class="metric-value">₪${data.metrics.totalRevenue.toFixed(
                2
              )}</div>
              <div class="metric-label">Total Revenue</div>
            </div>
            <div class="metric-card">
              <div class="metric-value">₪${data.metrics.totalPaid.toFixed(
                2
              )}</div>
              <div class="metric-label">Total Paid</div>
            </div>
            <div class="metric-card">
              <div class="metric-value">₪${data.metrics.totalOutstanding.toFixed(
                2
              )}</div>
              <div class="metric-label">Outstanding</div>
            </div>
            <div class="metric-card">
              <div class="metric-value">${data.metrics.totalTransactions}</div>
              <div class="metric-label">Transactions</div>
            </div>
          </div>
        `;
      case "detailed":
        return `
          <table>
            <thead>
              <tr>
                <th>Receipt #</th>
                <th>Date</th>
                <th>Patient</th>
                <th>Total</th>
                <th>Paid</th>
                <th>Outstanding</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              ${data.transactions
                .map(
                  (t) => `
                <tr>
                  <td>${t.receiptNumber}</td>
                  <td>${new Date(t.date).toLocaleDateString()}</td>
                  <td>${t.patient?.name || "Unknown"}</td>
                  <td>₪${t.totalAmount.toFixed(2)}</td>
                  <td>₪${t.paidAmount.toFixed(2)}</td>
                  <td>₪${(t.totalAmount - t.paidAmount).toFixed(2)}</td>
                  <td>${t.status}</td>
                </tr>
              `
                )
                .join("")}
            </tbody>
          </table>
        `;
      default:
        return "<p>Report content not available</p>";
    }
  };

  const formatCurrency = (amount) => `₪${amount.toFixed(2)}`;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-6">
          <FileText className="h-6 w-6 text-blue-600" />
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Financial Reports
            </h2>
            <p className="text-sm text-gray-600">
              Generate comprehensive financial reports and analytics
            </p>
          </div>
        </div>

        {/* Report Configuration */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Report Type
            </label>
            <select
              value={reportType}
              onChange={(e) => setReportType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="summary">Summary Report</option>
              <option value="detailed">Detailed Transactions</option>
              <option value="patient">Patient Revenue</option>
              <option value="service">Service Breakdown</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Start Date
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              End Date
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div className="flex items-end gap-2">
            <button
              onClick={() => exportReport("csv")}
              disabled={!reportData || loading}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Download className="h-4 w-4" />
              Export CSV
            </button>
            <button
              onClick={printReport}
              disabled={!reportData || loading}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Printer className="h-4 w-4" />
              Print
            </button>
          </div>
        </div>
      </div>

      {/* Report Content */}
      {loading ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-3 text-gray-600">Generating report...</span>
          </div>
        </div>
      ) : reportData ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {reportType.charAt(0).toUpperCase() + reportType.slice(1)} Report
            </h3>
            <p className="text-sm text-gray-600">
              {new Date(reportData.period.startDate).toLocaleDateString()} -{" "}
              {new Date(reportData.period.endDate).toLocaleDateString()}
            </p>
          </div>

          {/* Report-specific content */}
          {reportData.type === "summary" && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <DollarSign className="h-5 w-5 text-blue-600" />
                    <span className="text-sm font-medium text-blue-600">
                      Total Revenue
                    </span>
                  </div>
                  <div className="text-2xl font-bold text-gray-900">
                    {formatCurrency(reportData.metrics.totalRevenue)}
                  </div>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUp className="h-5 w-5 text-green-600" />
                    <span className="text-sm font-medium text-green-600">
                      Total Paid
                    </span>
                  </div>
                  <div className="text-2xl font-bold text-gray-900">
                    {formatCurrency(reportData.metrics.totalPaid)}
                  </div>
                </div>
                <div className="bg-red-50 p-4 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Activity className="h-5 w-5 text-red-600" />
                    <span className="text-sm font-medium text-red-600">
                      Outstanding
                    </span>
                  </div>
                  <div className="text-2xl font-bold text-gray-900">
                    {formatCurrency(reportData.metrics.totalOutstanding)}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="text-center p-4 border border-gray-200 rounded-lg">
                  <div className="text-2xl font-bold text-gray-900">
                    {reportData.metrics.totalTransactions}
                  </div>
                  <div className="text-sm text-gray-600">
                    Total Transactions
                  </div>
                </div>
                <div className="text-center p-4 border border-gray-200 rounded-lg">
                  <div className="text-2xl font-bold text-gray-900">
                    {reportData.metrics.uniquePatients}
                  </div>
                  <div className="text-sm text-gray-600">Unique Patients</div>
                </div>
                <div className="text-center p-4 border border-gray-200 rounded-lg">
                  <div className="text-2xl font-bold text-gray-900">
                    {formatCurrency(reportData.metrics.averageTransactionValue)}
                  </div>
                  <div className="text-sm text-gray-600">
                    Average Transaction
                  </div>
                </div>
              </div>
            </div>
          )}

          {reportData.type === "patient" && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="text-center p-4 border border-gray-200 rounded-lg">
                  <div className="text-2xl font-bold text-gray-900">
                    {reportData.summary.totalPatients}
                  </div>
                  <div className="text-sm text-gray-600">Total Patients</div>
                </div>
                <div className="text-center p-4 border border-gray-200 rounded-lg">
                  <div className="text-2xl font-bold text-gray-900">
                    {formatCurrency(reportData.summary.totalRevenue)}
                  </div>
                  <div className="text-sm text-gray-600">Total Revenue</div>
                </div>
                <div className="text-center p-4 border border-gray-200 rounded-lg">
                  <div className="text-2xl font-bold text-gray-900">
                    {formatCurrency(reportData.summary.averagePerPatient)}
                  </div>
                  <div className="text-sm text-gray-600">
                    Average per Patient
                  </div>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full border-collapse border border-gray-200">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="border border-gray-200 px-4 py-2 text-left">
                        Patient
                      </th>
                      <th className="border border-gray-200 px-4 py-2 text-right">
                        Revenue
                      </th>
                      <th className="border border-gray-200 px-4 py-2 text-right">
                        Paid
                      </th>
                      <th className="border border-gray-200 px-4 py-2 text-right">
                        Outstanding
                      </th>
                      <th className="border border-gray-200 px-4 py-2 text-center">
                        Transactions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.patients.slice(0, 20).map((patient, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="border border-gray-200 px-4 py-2">
                          {patient.name}
                        </td>
                        <td className="border border-gray-200 px-4 py-2 text-right">
                          {formatCurrency(patient.totalRevenue)}
                        </td>
                        <td className="border border-gray-200 px-4 py-2 text-right">
                          {formatCurrency(patient.totalPaid)}
                        </td>
                        <td className="border border-gray-200 px-4 py-2 text-right">
                          {formatCurrency(
                            patient.totalRevenue - patient.totalPaid
                          )}
                        </td>
                        <td className="border border-gray-200 px-4 py-2 text-center">
                          {patient.transactionCount}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
          <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No Report Generated
          </h3>
          <p className="text-gray-600">
            Configure your report settings and the report will be generated
            automatically.
          </p>
        </div>
      )}
    </div>
  );
};

export default FinancialReports;
